/**
 * Permission Cache with Async Fetch
 *
 * Implements a simple promise-based cache for permissions.
 * Handles the "first navigation before profile fetch" race condition.
 *
 * Pattern: If cache is fresh, return sync. If stale/missing, await fetch.
 *
 * @module utils/permissionCache
 */

import bbAxios from './bbAxios';
import { STORAGE_KEYS } from '../sessionConfig';

const PERMISSIONS_KEY = STORAGE_KEYS.PERMISSIONS;
const PERMISSIONS_TIMESTAMP_KEY = STORAGE_KEYS.PERMISSIONS_TS;
const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

// In-flight promise to prevent duplicate fetches
let fetchPromise = null;

/**
 * Ensure permissions are available - async with caching.
 * This is the primary method used in beforeLoad.
 *
 * @returns {Promise<Set<string>>} User permissions
 */
export async function ensurePermissions() {
  // 1. Check cache freshness
  const cached = getCachedPermissions();
  if (cached.fresh) {
    console.debug('[BB:Permissions] Cache hit (fresh)', {
      permissions: [...cached.permissions],
    });
    return cached.permissions;
  }

  // 2. If already fetching, wait for that promise
  if (fetchPromise) {
    console.debug('[BB:Permissions] Waiting for in-flight fetch...');
    return fetchPromise;
  }

  // 3. Fetch fresh permissions
  console.debug('[BB:Permissions] Cache stale/missing, fetching from API...');
  fetchPromise = fetchAndCachePermissions();
  try {
    const permissions = await fetchPromise;
    return permissions;
  } finally {
    fetchPromise = null;
  }
}

/**
 * Get cached permissions synchronously (for UI components).
 * Returns empty Set if not cached - use ensurePermissions() for guards.
 *
 * @returns {Set<string>} Cached permissions or empty Set
 */
export function getPermissionsSync() {
  try {
    const stored = localStorage.getItem(PERMISSIONS_KEY);
    if (!stored) return new Set();
    return new Set(JSON.parse(stored));
  } catch {
    return new Set();
  }
}

/**
 * Check if cache exists and is fresh.
 *
 * @returns {{ fresh: boolean, permissions: Set<string> }}
 */
function getCachedPermissions() {
  try {
    const stored = localStorage.getItem(PERMISSIONS_KEY);
    const timestamp = localStorage.getItem(PERMISSIONS_TIMESTAMP_KEY);

    if (!stored || !timestamp) {
      return { fresh: false, permissions: new Set() };
    }

    const age = Date.now() - parseInt(timestamp, 10);
    const fresh = age < STALE_TIME_MS;

    return {
      fresh,
      permissions: new Set(JSON.parse(stored)),
    };
  } catch {
    return { fresh: false, permissions: new Set() };
  }
}

/**
 * Fetch permissions from API and cache.
 *
 * @returns {Promise<Set<string>>}
 */
async function fetchAndCachePermissions() {
  try {
    console.debug('[BB:Permissions] Fetching from /api/v2/utility/auth/user...');
    const { data } = await bbAxios.get('/api/v2/utility/auth/user');
    const permissions = data.permissions || [];

    // Cache with timestamp
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
    localStorage.setItem(PERMISSIONS_TIMESTAMP_KEY, String(Date.now()));

    console.debug('[BB:Permissions] Fetched and cached:', permissions);
    return new Set(permissions);
  } catch (error) {
    console.error('[BB:Permissions] Failed to fetch permissions:', error);
    // Return stale cache if available, otherwise empty
    return getPermissionsSync();
  }
}

/**
 * Invalidate cache - forces next ensurePermissions() to fetch.
 */
export function invalidatePermissionCache() {
  console.debug('[BB:Permissions] Cache invalidated (marked stale)');
  localStorage.removeItem(PERMISSIONS_TIMESTAMP_KEY);
  fetchPromise = null;
}

/**
 * Clear all permission data (logout).
 */
export function clearPermissions() {
  console.debug('[BB:Permissions] Cleared all permission data (logout)');
  localStorage.removeItem(PERMISSIONS_KEY);
  localStorage.removeItem(PERMISSIONS_TIMESTAMP_KEY);
  fetchPromise = null;
}

/**
 * Store permissions directly (used after login profile fetch).
 *
 * @param {string[] | Set<string>} permissions - Permissions to store
 */
export function storePermissions(permissions) {
  try {
    const arr = permissions instanceof Set ? Array.from(permissions) : permissions;
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(arr));
    localStorage.setItem(PERMISSIONS_TIMESTAMP_KEY, String(Date.now()));
    console.debug('[BB:Permissions] Stored permissions:', arr);
  } catch (e) {
    console.warn('[permissionCache] Failed to store:', e);
  }
}
